import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { memo, createElement, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from 'styled-components';
import { WebsocketProvider } from 'y-websocket';
import Tiptap, { TiptapStyles, TiptapNodeStyles } from './Tiptap.js';
import { getYdocFromHtmlContent } from './utils/getYDocFromHtmlContent.js';
import * as Y from 'yjs';
import { useResourceNotepadDataContextSafe } from '../resource-detail/components/context/resourceDataContext.js';
import { useQueryNotepadResourceState } from '../resource-detail/queries/useQueryNotepadResourceState.js';
import { useQueryNotepadResourceSync } from '../resource-detail/queries/useQueryNotepadResourceSync.js';
import { HybridSyncProvider, HybridSyncProviderMode } from '../resource-detail/utils/HybridSyncProvider.js';
import { isWoodyError } from '../errors/errors.utils.js';
import { useQueryMultiplayerSession } from '../multiplayer/queries/useQueryMultiplayerSession.js';
import { getUserColorFromId } from '@fabric-app/utils/color';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { useSingletonRef } from '@fabric-app/hooks/useSingletonRef';
import { useUnmount } from '@fabric-app/hooks/useUnmount';
import { useOnWindowClose } from '@fabric-app/hooks/useOnWindowClose';
import { useOptimisticNotepadStateUpdate } from '../resource-detail/mutations/useMutationNotepadStateUpdate.js';
import { useReferencedFn } from '@fabric-app/hooks/useReferencedFn';
import { isObject } from '@fabric-app/utils/guards';
import { useSocketIOParentRoom } from '../socket.io/ParentRoom/SocketIOParentRoomContext.js';
import { NotepadUpdateFailsafeService } from './storage/NotepadUpdateFailsafeService.js';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { DocumentHtmlPreview } from '../resource-detail/components/DocumentHtmlPreview.js';
import EditorPreview from '../resources/components/EditorPreview/EditorPreview.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import BannerDisplay from '../alerts/BannerDisplay/BannerDisplay.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';

const Loader = styled(motion.div).withConfig({
  displayName: "MultiplayerEditor__Loader",
  componentId: "sc-480c6d13-0"
})([
  `position:absolute;width:100%;top:0;padding:1.5rem;display:flex;flex-direction:column;gap:0.75rem;justify-content:flex-start;align-items:flex-start;background:var(--fabric-color-bg-primary);`
]);
const WEBSOCKET_MAX_RETRIES = 3;
const TextSkeleton = styled(Skeleton).attrs((props) => {
  return {
    ...props,
    borderRadius: 6,
    height: 18
  };
}).withConfig({
  displayName: "MultiplayerEditor__TextSkeleton",
  componentId: "sc-480c6d13-1"
})([
  ``
]);
const TiptapEditorWrapper = /* @__PURE__ */ memo(({ tiptapProps, editable, initialValue, provider, yDoc, userAwareness, notepadKey, onChange }) => {
  return /* @__PURE__ */ createElement(Tiptap, {
    ...tiptapProps,
    onChange,
    onlineProvider: provider,
    yDoc,
    key: `tiptap-${notepadKey}`,
    userAwareness,
    editable,
    initialValue
  });
});
TiptapEditorWrapper.displayName = "TiptapEditorWrapper";
const LoaderComponent = /* @__PURE__ */ memo(() => /* @__PURE__ */ jsxs(Loader, {
  animate: {
    opacity: 1
  },
  initial: {
    opacity: 0
  },
  // we have a small delay on the exit to let Tiptap sync up with YJS, it is not perfect but it works
  exit: {
    opacity: 0,
    transition: {
      delay: 0.2,
      duration: 0.1
    }
  },
  children: [
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "93%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "91%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "87%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "98%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "64%"
    }),
    /* @__PURE__ */ jsx("span", {}),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "89%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "94%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "92%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "84%"
    }),
    /* @__PURE__ */ jsx(TextSkeleton, {
      width: "53%"
    })
  ]
}));
LoaderComponent.displayName = "LoaderComponent";
const MultiplayerEditor = ({ onOfflineChange, tiptapProps = {}, onModeChange: onHybridSyncProviderModeChange, onMultiplayerStateChange, onHasPendingChanges, onStateSynced, onChangeDebounce = 300 }) => {
  const { resource: notepad } = useResourceNotepadDataContextSafe();
  const notepadRef = useRef(notepad);
  notepadRef.current = notepad;
  const socketRoom = useSocketIOParentRoom();
  const isOnlineNotepad = !notepad.isDraft && !!(socketRoom?.connectedClients && socketRoom.connectedClients > 1);
  const notepadKey = notepad.draftId ?? notepad.id;
  const [isLoading, setIsLoading] = useState(!notepad.isDraft);
  const [isOutdated, setIsOutdated] = useState(false);
  const [hasPendingUpdates, setHasPendingUpdates] = useState(false);
  const debouncedHasPendingUpdates = useDebounce(hasPendingUpdates, 3e3);
  const optimisticNotepadStateUpdate = useOptimisticNotepadStateUpdate();
  const optimisticNotepadStateUpdateRef = useReferencedFn(optimisticNotepadStateUpdate);
  const yDocRef = useSingletonRef(() => new Y.Doc());
  useClientLayoutEffect(() => {
    const notepadId = notepad.id;
    const yDoc = yDocRef.current;
    if (notepad.isDraft || !notepadId || !yDoc) return;
    const instance = NotepadUpdateFailsafeService.getInstance();
    const onUpdate = (update, origin) => {
      if (origin instanceof HybridSyncProvider) return;
      instance.storeUpdate(notepadId, update);
    };
    yDoc.on("update", onUpdate);
    return () => {
      yDoc.off("update", onUpdate);
    };
  }, [
    yDocRef,
    notepad.id,
    notepad.isDraft
  ]);
  const hybridSyncProviderRef = useSingletonRef(() => new HybridSyncProvider({
    doc: yDocRef.current,
    debounceWait: onChangeDebounce,
    initialMode: isOnlineNotepad ? HybridSyncProviderMode.SemiOffline : HybridSyncProviderMode.FullyOffline
  }));
  const { data: notepadState, isLoading: notepadStateLoading, error: notepadStateError } = useQueryNotepadResourceState(notepad, {
    enabled: !notepad?.isDraft,
    enableMergeResync: tiptapProps.editable
  });
  const { data: syncUpdate, isLoading: syncUpdateLoading, isFetching: syncUpdateFetching, isFetchedAfterMount } = useQueryNotepadResourceSync(notepad, () => hybridSyncProviderRef.current.getDocState(), {
    enabled: !notepad?.isDraft && !notepad.isReadonly && !notepadStateLoading && !isOutdated && hybridSyncProviderRef.current.synced,
    refetchInterval: isOnlineNotepad ? false : 9e3,
    refetchOnMount: "always"
  });
  const { data: session, isLoading: isLoadingSession, refetch: refetchSession } = useQueryMultiplayerSession({
    resourceId: notepad.id
  }, {
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: isOnlineNotepad && !isOutdated
  });
  const websocketProviderRef = useSingletonRef(() => new WebsocketProvider("", notepad.id, yDocRef.current, {
    connect: false,
    // Max amount of time to retry connection on failure. It's an exponential backoff.
    maxBackoffTime: 4e3
  }));
  const websocketConnectionRetries = useRef(0);
  useEffect(() => {
    if (!isOnlineNotepad || isLoadingSession || !session) return;
    const webSocketProvider = websocketProviderRef.current;
    const hybridSyncProvider = hybridSyncProviderRef.current;
    webSocketProvider.serverUrl = session.url;
    webSocketProvider.roomname = notepad.id;
    webSocketProvider.params = {
      token: session.auth.token
    };
    webSocketProvider.shouldConnect = true;
    const onStatus = ({ status }) => {
      if (status === "connected") {
        websocketConnectionRetries.current = 0;
        return;
      }
      hybridSyncProvider.setMode(HybridSyncProviderMode.SemiOffline);
    };
    const onSynced = () => {
      hybridSyncProvider.setMode(HybridSyncProviderMode.Online);
      setIsLoading(false);
    };
    const onConnError = () => {
      websocketConnectionRetries.current += 1;
      if (websocketConnectionRetries.current > WEBSOCKET_MAX_RETRIES) {
        websocketConnectionRetries.current = 0;
        hybridSyncProvider.setMode(HybridSyncProviderMode.FullyOffline);
        refetchSession();
      } else {
        webSocketProvider.connect();
      }
    };
    webSocketProvider.on("status", onStatus);
    webSocketProvider.on("sync", onSynced);
    webSocketProvider.on("connection-error", onConnError);
    webSocketProvider.on("connection-close", onConnError);
    webSocketProvider.connect();
    return () => {
      webSocketProvider.off("status", onStatus);
      webSocketProvider.off("sync", onSynced);
      webSocketProvider.off("connection-error", onConnError);
      webSocketProvider.off("connection-close", onConnError);
      webSocketProvider.disconnect();
    };
  }, [
    hybridSyncProviderRef,
    websocketProviderRef,
    isLoadingSession,
    isOnlineNotepad,
    notepad.id,
    session,
    refetchSession
  ]);
  useEffect(() => {
    if (notepad.isDraft || isOutdated) return;
    const hybridSyncProvider = hybridSyncProviderRef.current;
    hybridSyncProvider.setMode(HybridSyncProviderMode.FullyOffline);
    const onUpdate = (update, hashes) => {
      onOfflineChange?.(update, hashes, isOnlineNotepad);
    };
    hybridSyncProvider.on("update", onUpdate);
    return () => {
      hybridSyncProvider.off("update", onUpdate);
    };
  }, [
    isOnlineNotepad,
    onOfflineChange,
    notepad.isDraft,
    hybridSyncProviderRef,
    isOutdated
  ]);
  const wasUpdatedAfterMountFetched = useRef(false);
  useEffect(() => {
    if (!notepadState || notepadStateLoading || isOutdated) return;
    const hybridSyncProvider = hybridSyncProviderRef.current;
    if ((!isFetchedAfterMount || wasUpdatedAfterMountFetched.current) && hybridSyncProvider.synced) return;
    if (!hybridSyncProvider.synced) hybridSyncProvider.initialize(notepadState);
    else if (isFetchedAfterMount && !wasUpdatedAfterMountFetched.current) {
      hybridSyncProvider.applyUpdate(notepadState);
      wasUpdatedAfterMountFetched.current = true;
    }
  }, [
    hybridSyncProviderRef,
    notepadState,
    notepadStateLoading,
    isFetchedAfterMount,
    isOutdated
  ]);
  useEffect(() => {
    if (!notepad.id || notepad.isDraft) return;
    const instance = NotepadUpdateFailsafeService.getInstance();
    instance.getUpdates(notepad.id).then((updates) => {
      if (updates.length > 0) {
        hybridSyncProviderRef.current.applyUpdate(Y.mergeUpdates(updates.map((u) => u.update)));
      }
    });
  }, [
    hybridSyncProviderRef,
    notepad
  ]);
  useEffect(() => {
    const hybridSyncProvider = hybridSyncProviderRef.current;
    if (notepadState || notepadStateLoading || isOnlineNotepad || notepad.isDraft || !isWoodyError(notepadStateError) || notepadStateError.status !== 404 || hybridSyncProvider.synced) return;
    const content = notepad.data.preview?.content;
    if (typeof content !== "string") return;
    const yDoc = getYdocFromHtmlContent(content);
    const update = Y.encodeStateAsUpdate(yDoc);
    hybridSyncProvider.initialize(update);
    onOfflineChange?.(update, [], isOnlineNotepad);
  }, [
    hybridSyncProviderRef,
    isOnlineNotepad,
    notepad.isDraft,
    notepadState,
    notepadStateError,
    notepadStateLoading,
    onOfflineChange,
    notepad.data.preview?.content
  ]);
  useEffect(() => {
    if (!syncUpdate || syncUpdateLoading || syncUpdateFetching) return;
    const hybridSyncProvider = hybridSyncProviderRef.current;
    hybridSyncProvider.applyUpdate(syncUpdate);
    const newState = hybridSyncProvider.getDocState();
    optimisticNotepadStateUpdateRef.current({
      resource: notepadRef.current,
      update: newState
    });
  }, [
    hybridSyncProviderRef,
    optimisticNotepadStateUpdateRef,
    syncUpdate,
    syncUpdateFetching,
    syncUpdateLoading
  ]);
  useUnmount(() => {
    const websocketProvider = websocketProviderRef.current;
    websocketProvider.destroy();
    const pendingUpdate = hybridSyncProviderRef.current.destroy();
    if (pendingUpdate) {
      onOfflineChange?.(pendingUpdate.update, pendingUpdate.hashes);
    }
  }, {
    avoidStrictMode: true
  });
  useEffect(() => {
    const hybridSyncProvider = hybridSyncProviderRef.current;
    const onModeChange = (mode) => {
      onHybridSyncProviderModeChange?.(mode);
      switch (mode) {
        case HybridSyncProviderMode.FullyOffline:
          onMultiplayerStateChange?.("OFFLINE");
          break;
        case HybridSyncProviderMode.SemiOffline:
          onMultiplayerStateChange?.("CONNECTING");
          break;
        case HybridSyncProviderMode.Online:
          onMultiplayerStateChange?.("SYNCED");
          break;
      }
    };
    const onSynced = () => {
      onMultiplayerStateChange?.("OFFLINE");
      onStateSynced?.();
      setIsLoading(false);
    };
    const onOutdated = () => {
      onMultiplayerStateChange?.("OFFLINE");
      setIsOutdated(true);
      setIsLoading(true);
      setHasPendingUpdates(false);
    };
    const onPendingUpdatesChange = (pendingUpdates) => {
      onHasPendingChanges?.(pendingUpdates.length > 0);
      setHasPendingUpdates(pendingUpdates.length > 0);
    };
    hybridSyncProvider.on("modeChange", onModeChange);
    hybridSyncProvider.on("synced", onSynced);
    hybridSyncProvider.on("pendingUpdatesChange", onPendingUpdatesChange);
    hybridSyncProvider.on("outdated", onOutdated);
    return () => {
      hybridSyncProvider.off("modeChange", onModeChange);
      hybridSyncProvider.off("synced", onSynced);
      hybridSyncProvider.off("pendingUpdatesChange", onPendingUpdatesChange);
      hybridSyncProvider.off("outdated", onOutdated);
    };
  }, [
    hybridSyncProviderRef,
    onHasPendingChanges,
    onMultiplayerStateChange,
    onHybridSyncProviderModeChange,
    onStateSynced,
    setHasPendingUpdates
  ]);
  useOnWindowClose(hasPendingUpdates || debouncedHasPendingUpdates, () => {
    const hybridSyncProvider = hybridSyncProviderRef.current;
    const pendingUpdate = hybridSyncProvider.consumePendingUpdates();
    if (pendingUpdate) {
      onOfflineChange?.(pendingUpdate.update, pendingUpdate.hashes);
    }
  });
  const onTiptapChange = useCallback((content, ydoc, transaction) => {
    const ySyncMeta = transaction?.getMeta("y-sync$");
    const isUndoRedoOperation = Boolean(isObject(ySyncMeta) && "isUndoRedoOperation" in ySyncMeta && ySyncMeta.isUndoRedoOperation);
    if (
      // don't send transactions that are just noise
      ySyncMeta && !isUndoRedoOperation || /**
      * if it's a draft we don't need to send $nodeInfo transactions, this is from our custom
      * Node Info extension which applies unique IDs to nodes that don't have them. While it's a draft
      * this would cause a notepad to be created before the user interacts with the editor because it is simply
      * setting up the ID for the first elements.
      */
      (notepad?.isDraft || !notepad?.id) && transaction?.getMeta("$nodeInfo")
    ) return;
    tiptapProps.onChange?.(content, ydoc, transaction);
  }, [
    notepad?.isDraft,
    notepad?.id,
    tiptapProps
  ]);
  const userAwarenessValue = useMemo(() => {
    if (!session) return null;
    return {
      name: session.auth.user.name,
      color: session.auth.user.id ? getUserColorFromId(session.auth.user.id) : "#000000"
    };
  }, [
    session
  ]);
  const isEditable = useMemo(() => {
    return !session?.auth.user.readOnly && !notepad.isReadonly && tiptapProps?.editable !== false;
  }, [
    session?.auth.user.readOnly,
    notepad.isReadonly,
    tiptapProps?.editable
  ]);
  const initialValue = useMemo(() => {
    return notepad.isDraft ? tiptapProps.initialValue : void 0;
  }, [
    notepad.isDraft,
    tiptapProps.initialValue
  ]);
  return /* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsx(AnimatePresence, {
      children: isOutdated ? /* @__PURE__ */ jsxs(OutdatedContainer, {
        children: [
          /* @__PURE__ */ jsx(BannerDisplay, {
            alert: outdatedAlert
          }),
          /* @__PURE__ */ jsxs(ScrollArea.Default, {
            scrollbarVariant: "subtle",
            children: [
              /* @__PURE__ */ jsx(OutdatedEditorPreview, {
                data: {
                  content: notepad.data.preview?.content
                }
              }),
              /* @__PURE__ */ jsx(FadeOut, {})
            ]
          })
        ]
      }) : !isLoading ? /* @__PURE__ */ jsx(TiptapEditorWrapper, {
        tiptapProps,
        onChange: onTiptapChange,
        provider: websocketProviderRef.current,
        yDoc: yDocRef.current,
        notepadKey,
        userAwareness: userAwarenessValue,
        editable: isEditable,
        initialValue
      }) : /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(LoaderComponent, {}),
          /* @__PURE__ */ jsx(DocumentHtmlPreview, {
            resource: notepad
          })
        ]
      })
    })
  });
};
const outdatedAlert = {
  id: "outdated-editor",
  dismissable: false,
  title: "You're using an old version of Fabric's editor. Refresh the app to edit",
  action: {
    type: "refresh",
    label: "Refresh"
  },
  content: null,
  severity: "warning",
  type: "banner",
  countdown: null
};
const OutdatedContainer = styled(Flex).attrs((p) => {
  return {
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "MultiplayerEditor__OutdatedContainer",
  componentId: "sc-480c6d13-2"
})([
  `position:relative;min-height:0;`
]);
const OutdatedEditorPreview = styled(EditorPreview).attrs((p) => {
  return {
    ...p,
    unstyled: true,
    limit: 1e3
  };
}).withConfig({
  displayName: "MultiplayerEditor__OutdatedEditorPreview",
  componentId: "sc-480c6d13-3"
})([
  `font-size:1rem;opacity:0.8;> div{`,
  ` `,
  `}ul[data-type='taskList']{list-style:none;padding-left:0;li{display:flex;align-items:flex-start;gap:4px;margin-top:0.25rem;input{height:30px;margin-top:-3px;}input[checked] ~ div p{text-decoration:line-through rgba(`,
  `,0.5);}}ul{padding-left:1em;}}`
], TiptapStyles, TiptapNodeStyles, cssVar["color-text-note-primary-rgb"]);
const FadeOut = styled.div.withConfig({
  displayName: "MultiplayerEditor__FadeOut",
  componentId: "sc-480c6d13-4"
})([
  `position:absolute;top:0;left:0;right:0;bottom:0;background:linear-gradient( to bottom,rgba(`,
  `,0),rgba(`,
  `,0) 50%,rgba(`,
  `,1) 97% );pointer-events:none;z-index:1;`
], cssVar["color-bg-primary-rgb"], cssVar["color-bg-primary-rgb"], cssVar["color-bg-primary-rgb"]);

export { MultiplayerEditor as default };
//# sourceMappingURL=MultiplayerEditor.js.map
